import { useAuth } from "@dl/web-core-js/auth";
import { Button, Select, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Trash2, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCTION_DOCUMENT_KINDS,
  ProductionOrderDocumentClient,
  type ProductionDocumentKind,
  type ProductionOrderDocumentDTO,
} from "../../../api/cutJobs";
import { refusalMessage } from "../../../api/manufacturing";
import { CUT_JOBS_KEY, documentKindLabel } from "./cutJobModel";

/**
 * The files of a cut job or an order (Cut Rite's cutting and foam plans):
 * each opens in a new tab; with an owner, more can be uploaded with their
 * kind, and removed.
 */
export function CutJobDocuments({
  documents,
  owner,
  defaultKind = "CUTRITE_CUT",
  large = false,
}: {
  documents: ProductionOrderDocumentDTO[];
  /** Where an upload goes; none: read only (the station). */
  owner?: { cutJobId?: number; productionOrderId?: number } | null;
  defaultKind?: ProductionDocumentKind;
  /** The station's touch size. */
  large?: boolean;
}) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new ProductionOrderDocumentClient(http), [http]);
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<ProductionDocumentKind>(defaultKind);
  const refresh = () => queryClient.invalidateQueries({ queryKey: CUT_JOBS_KEY });
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await client.upload(owner!, file, kind);
    },
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: number) => client.remove(id), onSuccess: refresh, onError: fail });
  const open = async (document: ProductionOrderDocumentDTO) => {
    try {
      window.open(await client.link(document.id), "_blank", "noopener");
    } catch (failure) {
      fail(failure);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {documents.length === 0 ? (
        <p className="text-[13px] text-ink-muted">{t("document.none", "No document yet.")}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {documents.map((document) => (
            <li key={document.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void open(document)}
                className={
                  large
                    ? "flex min-h-14 flex-1 items-center gap-3 rounded-lg border border-line bg-surface px-4 text-left text-[17px] hover:bg-surface-sunk"
                    : "flex flex-1 items-center gap-2 text-left text-[13px] text-accent hover:underline"
                }
              >
                <FileText className={large ? "size-6" : "size-4"} />
                <span className="min-w-0 flex-1 truncate">{document.file_name}</span>
                <span className="text-ink-muted">{documentKindLabel(document.kind, t)}</span>
              </button>
              {owner && canWrite && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-4" />}
                  aria-label={t("document.remove", "Remove")}
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: t("document.remove.title", "Remove {{name}}?", { name: document.file_name }),
                        message: t("document.remove.message", "The file is no longer shown on the job, its lots or at the station."),
                        confirmLabel: t("document.remove", "Remove"),
                        variant: "danger",
                      });
                      if (ok) remove.mutate(document.id);
                    })();
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {owner && canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-56"
            options={PRODUCTION_DOCUMENT_KINDS.map((value) => ({ value, label: documentKindLabel(value, t) }))}
            value={kind}
            onChange={(event) => setKind(event.target.value as ProductionDocumentKind)}
            aria-label={t("document.kind", "Kind")}
          />
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              if (files.length > 0) upload.mutate(files);
            }}
          />
          <Button size="sm" variant="secondary" icon={<Upload className="size-4" />} loading={upload.isPending} onClick={() => input.current?.click()}>
            {t("document.upload", "Upload")}
          </Button>
        </div>
      )}
    </div>
  );
}
